/**
 * PortalClientsPage — tuile Cabinet « Portail clients » (Batch #14.260).
 * Fusion de « Comptes clients » (comptes, activation, accès, MDP) et « Gestion des accès »
 * (tuiles visibles par dossier Master). Deux onglets + passerelle compte → dossier.
 *   /admin/portail-clients?tab=comptes | ?tab=acces&master_id=…
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Globe, Users, Lock } from "lucide-react";
import { api } from "@/api";
import AccountsTab from "./portailClients/AccountsTab";
import AccessTab from "./portailClients/AccessTab";

const TABS = [
  { id: "comptes", label: "Comptes clients", desc: "Activer, envoyer accès, réinitialiser", icon: Users, tone: "text-cyan-700 border-cyan-600" },
  { id: "acces", label: "Accès aux tuiles", desc: "Tuiles visibles par dossier", icon: Lock, tone: "text-rose-700 border-rose-600" },
];

export default function PortalClientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "comptes";
  const [masters, setMasters] = useState([]);

  useEffect(() => {
    api.get("/masters").then((r) => setMasters(r.data?.items || [])).catch(() => setMasters([]));
  }, []);

  // client_id → master_id (compte principal ou conjoint) pour la passerelle Comptes → Accès
  const masterByClient = useMemo(() => {
    const m = {};
    masters.forEach((x) => {
      if (x.primary_user_id) m[x.primary_user_id] = x.id;
      if (x.secondary_user_id) m[x.secondary_user_id] = x.id;
    });
    return m;
  }, [masters]);

  // client_id → true si aucune tuile cochée dans sa matrice (le client verrait un portail vide)
  const emptyTilesByClient = useMemo(() => {
    const noTiles = (p) => !Object.values(p || {}).some((v) => v === true);
    const m = {};
    masters.forEach((x) => {
      if (x.primary_user_id && noTiles(x.portal_permissions_primary)) m[x.primary_user_id] = true;
      if (x.secondary_user_id && x.secondary_portal_enabled && noTiles(x.portal_permissions_secondary)) m[x.secondary_user_id] = true;
    });
    return m;
  }, [masters]);

  const setTab = (id, extra = {}) => setSearchParams({ tab: id, ...extra });

  return (
    <div className="max-w-6xl mx-auto px-4 py-2" data-testid="portal-clients-page">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
          <Globe className="w-5 h-5 text-cyan-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Portail clients</h1>
          <p className="text-sm text-gray-500">Comptes, accès et tuiles visibles sur le portail</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-5" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id, t.id === "acces" && searchParams.get("master_id") ? { master_id: searchParams.get("master_id") } : {})}
            className={`px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 border-b-2 -mb-px transition ${
              tab === t.id ? t.tone : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
            data-testid={`portal-clients-tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
            <span className="hidden sm:inline text-[11px] font-normal text-gray-400">· {t.desc}</span>
          </button>
        ))}
      </div>

      {tab === "comptes" && (
        <AccountsTab
          masterByClient={masterByClient}
          emptyTilesByClient={emptyTilesByClient}
          onOpenAccess={(masterId) => setTab("acces", { master_id: masterId })}
        />
      )}
      {tab === "acces" && <AccessTab masters={masters} />}
    </div>
  );
}
